import {Injectable} from '@angular/core';
import {Market} from '../dto/market';
import {EMPTY, forkJoin, Observable, of} from 'rxjs';
import {MatSelectChange} from '@angular/material';
import {map} from 'rxjs/internal/operators';
import {BaseService} from './base.service';
import {AuthService} from './auth.service';
import {MarketLevel} from '../dto/market-level';
import {User} from '../dto/user';

@Injectable({providedIn: 'root'})
export class MarketService {

    loggedUser: User;
    marketLevels: MarketLevel[] = [];
    markets = [];
    selectedMarket: Market[] = [];
    userRank: number;

    constructor(private auth: AuthService, private service: BaseService) {
        this.loggedUser = this.auth.user;
        this.marketLevels = this.loggedUser.MarketLevelHierarchy;
        if (this.marketLevels) {
            this.marketLevels.forEach(pMarketLevel => {
                this.markets[pMarketLevel.Rank] = [];
            });
        }
        if (this.loggedUser.UserRole.MarketLevel) {
            this.userRank = this.loggedUser.UserRole.MarketLevel.Rank;
        } else {
            this.userRank = 0;
        }
    }

    prepareUserMarket(): Market[] {
        if (this.marketLevels) {
            this.marketLevels.forEach(pMarketLevel => {
                this.selectedMarket[pMarketLevel.Rank] = null;
            });
        }
        if (this.loggedUser.UserMarketList) {
            if (this.loggedUser.UserMarketList.length == 1) {
                this.loggedUser.UserMarketList.forEach(pMarket => {
                    if (pMarket.Market.ParentList) {
                        pMarket.Market.ParentList.forEach(pParentMarket => {
                            this.selectedMarket [pParentMarket.MarketLevel.Rank] = pParentMarket;
                        });
                    }
                    this.selectedMarket[this.userRank] = this.loggedUser.UserMarketList[0].Market;
                });
            }
        }
        return this.selectedMarket;
    }

    marketChangeListener(pEvent: MatSelectChange, pMarketLevelRank: number, currentSelectedMarkets: Market[]): Observable<Market[]> {
        const selectedMarkets = [];
        this.marketLevels.forEach(pMarketLevel => {
            selectedMarkets[pMarketLevel.Rank] = null;
        });
        let rank = 0;

        if (pEvent.value) {
            return this.service.getMarketDetails(pEvent.value.Id).pipe(
                map(pMarket => {
                    if (pMarket.ParentList) {
                        for (let i = pMarket.ParentList.length - 1; i >= 0; i--) {
                            selectedMarkets[pMarket.ParentList[i].MarketLevel.Rank] = pMarket.ParentList[i];
                            rank = pMarket.ParentList[i].MarketLevel.Rank;
                        }
                        selectedMarkets[rank + 1] = pMarket;
                    } else {
                        for (let i = 0; i < selectedMarkets.length; i++) {
                            if (pMarket.MarketLevel.Rank == i) {
                                selectedMarkets[i] = pMarket;
                            }
                        }
                    }
                    return selectedMarkets;
                })
            );
        } else {
            for (let i = pMarketLevelRank; i <= this.marketLevels.length; i++) {
                currentSelectedMarkets[i] = null;
            }
            if (this.userRank > 1) {
                let selectedMarket = new Market();
                for (let i = currentSelectedMarkets.length - 1; i >= 0; i--) {
                    if (currentSelectedMarkets[i]) {
                        selectedMarket = currentSelectedMarkets[i];
                        break;
                    }
                }
                if (selectedMarket.MarketLevel) {
                    if (selectedMarket.MarketLevel.Rank < this.userRank) {
                        for (let i = 0; i < currentSelectedMarkets.length; i++) {
                            currentSelectedMarkets[i] = null;
                        }
                    }
                }
            }
            return of(currentSelectedMarkets);
        }
    }

    getSelectedLevelMarkets(marketLevel: MarketLevel, currentSelectedMarkets: Market[]): Observable<Market[]> {
        const marketLevelId = marketLevel.Id;
        let marketId = null;
        let market = new Market();

        for (let i = currentSelectedMarkets.length - 1; i >= 0; i--) {
            if (currentSelectedMarkets[i]) {
                marketId = currentSelectedMarkets[i].Id;
                market = currentSelectedMarkets[i];
                break;
            }
        }
        if (marketId && marketLevelId != market.MarketLevel.Id && marketLevel.Rank > market.MarketLevel.Rank) {
            return this.service.getMarketMiniList(marketLevelId, marketId);
        } else {
            return EMPTY;
        }
    }

    getSelectedMarketList(pSelectedMarket: Market[]): Observable<Market[]> {
        const observables = [];
        for (let i = 1; i < pSelectedMarket.length; i++) {
            if (pSelectedMarket[i]) {
                const marketLevelId = pSelectedMarket[i].MarketLevel.Id;
                let marketId;
                if (i == 1 || pSelectedMarket[i].MarketLevel.Rank <= this.userRank) {
                    marketId = null;
                } else {
                    marketId = pSelectedMarket[i - 1].Id;
                }
                observables.push(this.service.getMarketMiniList(marketLevelId, marketId));
            } else {
                const marketLevelId = this.loggedUser.MarketLevelHierarchy[i - 1].Id;
                const marketId = pSelectedMarket[i - 1] ? pSelectedMarket[i - 1].Id : null;
                observables.push(this.service.getMarketMiniList(marketLevelId, marketId));
            }
        }
        return forkJoin(observables).pipe(
            map(responses => {
                responses.forEach((pResponse, index) => {
                    if (pSelectedMarket[index + 1]) {
                        this.markets[pSelectedMarket[index + 1].MarketLevel.Rank] = pResponse;
                    } else {
                        this.markets[this.loggedUser.MarketLevelHierarchy[index].Rank] = pResponse;
                    }
                });
                return this.markets;
            })
        );
    }

    getInitialMarketList(): Observable<any> {
        const observables = this.marketLevels.map((pMarketLevel) =>
            this.service.getMarketMiniList(pMarketLevel.Id)
        );
        return forkJoin(observables);
    }
}

